<template>
  <validation-observer
    ref="formUserEdit"
    #default="{ handleSubmit }"
  >
    <b-form
      class="auth-login-form mt-2"
      @submit.prevent="handleSubmit(saveUserForm)"
    >
      <!-- Media -->
      <b-media class="mb-2">
        <template #aside>
          <b-avatar
            ref="previewEl"
            :src="userData.avatar"
            :text="avatarText(userData.full_name)"
            :variant="`light-${resolveUserRoleVariant(userData.role)}`"
            size="90px"
            rounded
          />
        </template>
        <h4 class="mb-1">
          {{ userData.full_name }} ({{ userData.username }})
        </h4>
        <div class="d-flex flex-wrap">
          <b-button
            variant="primary"
            class="d-none"
            @click="$refs.refInputEl.click()"
          >
            <input
              ref="refInputEl"
              type="file"
              @input="inputImageRenderer"
            >
            <span class="d-none d-sm-inline">{{ $t('Upload') }}</span>
            <feather-icon
              icon="EditIcon"
              class="d-inline d-sm-none"
            />
          </b-button>
          <b-button
            variant="outline-secondary"
            class="ml-0"
            @click="removeAvatar"
          >
            <span class="d-none d-sm-inline"> Xóa avatar </span>
            <feather-icon
              icon="TrashIcon"
              class="d-inline d-sm-none"
            />
          </b-button>
        </div>
      </b-media>

      <!-- User Info: Input Fields -->

      <b-row>

        <!-- Field: Full Name -->
        <b-col
          cols="12"
          md="4"
        >
          <validation-provider
            #default="{ errors }"
            name="full_name"
            rules="required|max:30"
          >
            <b-form-group
              :label="$t('Full Name')"
              label-for="full-name"
            >
              <b-form-input
                id="full-name"
                v-model="userData.full_name"
                name="full_name"
                :state="errors.length > 0 ? false : null"
                placeholder=""
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Field: Phone Number -->
        <b-col
          cols="12"
          md="4"
        >
          <validation-provider
            #default="{ errors }"
            name="phone"
            rules="max:20"
          >
            <b-form-group
              :label="$t('Phone')"
              label-for="phone"
            >
              <b-form-input
                id="phone"
                v-model="userData.phone"
                v-mask="{ mask: '0999999999[9]', greedy: true }"
                :state="errors.length > 0 ? false : null"
                type="number"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Field: Email -->
        <b-col
          cols="12"
          md="4"
        >
          <validation-provider
            #default="{ errors }"
            name="email"
            rules="required|max:150"
          >
            <b-form-group
              label="Email"
              label-for="email"
            >
              <b-form-input
                id="email"
                v-model="userData.email"
                v-mask="{ alias: 'email' }"
                :state="errors.length > 0 ? false : null"
                type="email"
                trim
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Field: Ngày sinh -->
        <b-col
          cols="12"
          md="4"
        >
          <validation-provider
            #default="{ errors }"
            name="birthday"
            rules="max:10"
          >
            <b-form-group
              :label="$t('Birthday')"
              label-for="birthday"
            >
              <b-form-input
                id="birthday"
                v-model="userData.birthday"
                v-mask="{ mask: '99/99/9999', greedy: true }"
                :state="errors.length > 0 ? false : null"
                trim
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Field: GENDER -->
        <b-col
          cols="12"
          md="4"
        >
          <validation-provider
            #default="{ errors }"
            name="gender"
          >
            <b-form-group
              :label="$t('Gender')"
              label-for="gender"
            >
              <template #label>
                <label class="d-block font-weight-bolder">{{ $t('Gender') }} <span class="text-danger">*</span></label>
              </template>
              <v-select
                v-model="userData.gender"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="genderOptions"
                :reduce="(val) => val.id"
                input-id="gender"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Field: Alias -->
        <b-col
          cols="12"
          md="4"
        >
          <validation-provider
            #default="{ errors }"
            name="alias"
            rules="required|max:100"
          >
            <b-form-group
              :label="$t('Alias')"
              label-for="alias"
            >
              <b-form-input
                id="alias"
                v-model="userData.alias"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Timezone -->
        <b-col
          cols="12"
          md="4"
        >
          <validation-provider
            #default="{ errors }"
            :name="$t('Time zone')"
            rules="required"
          >
            <b-form-group>
              <template #label>
                <label class="d-block font-weight-bolder">{{ $t('Time zone') }} <span class="text-danger">*</span></label>
              </template>
              <v-select
                v-model="userData.timezone"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="timezones"
                :reduce="(val) => val.value"
                label="label"
                input-id="user-timezone"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>

        <!-- Field: Phòng ban -->
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group
            :label="$t('Department')"
            label-for="user-department"
          >
            <!-- <v-select
              v-model="userData.department_id"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="departmentsData"
              :reduce="(val) => val.id"
              label="name"
              :clearable="false"
              input-id="user-department"
            /> -->
            <treeselect
              v-model="userData.department_id"
              :multiple="false"
              :options="departmentsData"
              :disable-branch-nodes="false"
              :default-expand-level="5"
              search-nested
              :clearable="true"
              no-results-text="Không tìm thấy kết quả"
              no-options-text="Không có lựa chọn nào"
              placeholder="Chọn phòng"
              loading-text="Đang tải"
              search-prompt-text="Nhập tên"
              :normalizer="normalizerTreeList"
              :always-open="false"
            />
          </b-form-group>
        </b-col>
        <!-- Field: Phòng ban -->
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group :label="$t('Position')">
            <v-select
              v-model="userData.position_id"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="positionsData"
              :reduce="(val) => val.id"
              label="name"
              :clearable="false"
              input-id="user-position"
            />
          </b-form-group>
        </b-col>

        <!-- Field: Status -->
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group
            :label="$t('Status')"
            label-for="user-status"
          >
            <v-select
              v-model="userData.status"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="statusOptions"
              :reduce="(val) => val.id"
              :clearable="false"
              input-id="user-status"
            />
          </b-form-group>
        </b-col>

        <!-- Field: Status -->
        <b-col
          cols="12"
          md="4"
        >
          <!-- File Storage Quota -->
          <validation-provider
            #default="{ errors }"
            name="Giới hạn dung lượng lưu trữ file"
            rules="required|number-min-zero"
          >
            <b-form-group
              label="Giới hạn dung lượng lưu trữ file"
              label-for="file_storage_quota"
            >
              <template #label>
                <label class="d-block font-weight-bolder">Giới hạn dung lượng lưu trữ file (MB). Đặt bằng 0 để không giới hạn.<span class="text-danger">*</span></label>
              </template>
              <b-form-input
                id="file_storage_quota"
                v-model="userData.file_storage_quota"
                type="number"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </b-col>
      </b-row>
      <!-- Action Buttons -->
      <b-button
        variant="secondary"
        class="mr-2"
        :to="{ name: 'users-list' }"
      >
        {{ $t('Back') }}
      </b-button>
      <b-button
        type="submit"
        variant="primary"
        class="mb-1 mb-sm-0 mr-0 mr-sm-1"
        :block="$store.getters['app/currentBreakPoint'] === 'xs'"
        :disabled="loading"
      >
        {{ $t('Save Changes') }}
      </b-button>
    </b-form>
  </validation-observer>
</template>

<script>
import {
  BButton,
  BMedia,
  BAvatar,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BForm,
} from 'bootstrap-vue';
import { avatarText } from '@core/utils/filter';
import vSelect from 'vue-select';
import { useInputImageRenderer } from '@core/comp-functions/forms/form-utils';
import { ref } from '@vue/composition-api';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import {
  required, min, max, numberMinZero,
} from '@validations';
import { showToast } from '@core/utils/utils';
import router from '@/router';
import { timezones } from '@/libs/constants';
import Treeselect from '@riophae/vue-treeselect'
import useUsers from './useUsers';
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import useDepartments from '../department/useDepartments'

export default {
  components: {
    BButton,
    BMedia,
    BAvatar,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    vSelect,
    Treeselect,
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    userData: {
      type: Object,
      required: true,
    },
    positionsData: {
      type: Array,
      required: true,
    },
    departmentsData: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      required,
      min,
      max,
      numberMinZero,
      timezones,
      loading: false,
    }
  },
  setup(props) {
    const { resolveUserRoleVariant, statusOptions, genderOptions } = useUsers();

    const permissionsData = [
      {
        module: 'Admin',
        read: true,
        write: false,
        create: false,
        delete: false,
      },
      {
        module: 'Staff',
        read: false,
        write: true,
        create: false,
        delete: false,
      },
      {
        module: 'Author',
        read: true,
        write: false,
        create: true,
        delete: false,
      },
      {
        module: 'Contributor',
        read: false,
        write: false,
        create: false,
        delete: false,
      },
      {
        module: 'User',
        read: false,
        write: false,
        create: false,
        delete: true,
      },
    ];

    // ? Demo Purpose => Update image on click of update
    const refInputEl = ref(null);
    const previewEl = ref(null);
    const selectedGender = ref(2);
    const selectedStatus = ref(0);

    const { inputImageRenderer } = useInputImageRenderer(
      refInputEl,
      (base64) => {
        // eslint-disable-next-line no-param-reassign
        props.userData.avatar = base64;
      },
    );

    const {
      normalizerTreeList,
    } = useDepartments();

    return {
      resolveUserRoleVariant,
      avatarText,
      statusOptions,
      genderOptions,
      permissionsData,

      refInputEl,
      previewEl,
      inputImageRenderer,
      selectedGender,
      selectedStatus,

      normalizerTreeList,
    };
  },

  methods: {
    removeAvatar() {
      this.$store.dispatch('users/removeAvatar', { id: this.userData.id })
        .then(response => {
          if (response.status === 200) {
            if (response.data.code === 200) {
              showToast(response.data.message, { variant: 'success' });
            } else {
              showToast(response.data.message);
            }
            this.userData.avatar = '';
          }
        })
    },
    saveUserForm() {
      this.$refs.formUserEdit.validate().then((success) => {
        if (success) {
          this.loading = true;
          this.$store.dispatch('users/updateUser', {
            id: this.userData.id,
            userData: {
              full_name: this.userData.full_name,
              email: this.userData.email,
              phone: this.userData.phone,
              gender: this.userData.gender,
              birthday: this.userData.birthday,
              alias: this.userData.alias,
              timezone: this.userData.timezone,
              department_id: this.userData.department_id,
              position_id: this.userData.position_id,
              status: this.userData.status,
              file_storage_quota: this.userData.file_storage_quota,
              update_type: 'info',
            },
          })
            .then((response) => {
              this.loading = false;
              if (response.data.code === 200) {
                showToast(response.data.message, { variant: 'success' })
                if (response.data.code === 200) {
                  router.push({ name: 'users-list' })
                }
              } else {
                showToast(response.data.message)
              }
            })
            .catch((error) => {
              this.loading = false;
            });
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/base/components/treeselectlist.scss';
</style>
